<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import unitService from '@/services/unit.service'
import UnitDrawer from '@/views/masters/unit/drawer/UnitDrawer.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

const searchQuery = ref('')
const isConfirmDialogVisible = ref(false)

definePage({
  meta: {
    name: 'master-unit',
    subject: 'global.menu',
    action: 'master_unit',
  },
})

const snackbar = useToast()

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const errors = ref({
  name: [],
  code: [],
  display_name: [],
  status: [],
})

const onError = error => {
  if (error.data){
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  {
    title: 'Code',
    key: 'code',
  },
  {
    title: 'Name',
    key: 'name',
  },
  {
    title: 'Display Name',
    key: 'display_name',
  },
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const {
  data: unitsData,
  execute: fetchUnits,
} =  await useApi(createUrl('/masters/units', {
  query: {
    paged: 1,
    q: searchQuery,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const units = computed(() => unitsData.value?.data)
const totalUnits = computed(() => unitsData.value?.meta?.total)

const updateUnit = data => {
  // see is there any update or not
  if (data.id){
    var isUpdated = false
    Object.keys(data).forEach(key => {
      if (unitDrawerData.value[key] !== data[key]){
        isUpdated = true
      }
    })
  }

  if (!isUpdated){
    isUnitDrawerVisible.value = false
    
    return
  }
  unitService.updateUnit(data.id, data, ()=>
  {
    fetchUnits()
    isUnitDrawerVisible.value = false
  }, onError)
}

const saveUnit = data => {
  unitService.addUnit(data, ()=>
  {
    fetchUnits()
    isUnitDrawerVisible.value = false
  }, onError)
}

const isUnitDrawerVisible = ref(false)
const unitDrawerData = ref()
const unitDeleteData = ref()
const unitDrawerType = ref('Add')

const showUnit = (unitData, type) => {
  unitDrawerData.value = unitData
  unitDrawerType.value = type  
  isUnitDrawerVisible.value = true
}

const deleteUnit = async (confirm, data) => {
  if(data?.id && confirm ){
    await unitService.deleteUnit(data.id, () => {
      fetchUnits()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = unitData => {
  unitDeleteData.value = unitData
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value){
    unitDeleteData.value = {}
  }
})

// ===== Import Unit Functionality =====
const refImportUnitForm = ref()
const importUnitFile = ref()
const importUnitErrors = ref()
const isImportUnitLoading = ref(false)
const isImportUnitModal = ref(false)

const submitImportUnit = async () => {
  if (!importUnitFile.value) {
    importUnitErrors.value = { file: ["Please select a file to import."] }

    return
  }
  isImportUnitLoading.value = true
  importUnitErrors.value = null

  try {
    await unitService.import(
      {
        file: importUnitFile.value,
      },
      () => {
        // onSuccess callback
        snackbar.success("Units imported successfully.")
        isImportUnitModal.value = false
        fetchUnits() // Refresh the unit list
        importUnitFile.value = null // Reset file input
      },
      error => {
        // onError callback
        importUnitErrors.value = error.data?.errors || error._data?.message || {
          message:
            error.data?.message || "File upload failed. Please try again.",
        }
      },
    )
  } catch (e) {
    console.error("Error during unit import submission:", e)
    snackbar.error(
      "An unexpected error occurred while submitting the import request.",
    )
    if (!importUnitErrors.value)
      importUnitErrors.value = { message: "Import submission failed." }
  } finally {
    isImportUnitLoading.value = false
  }
}

const downloadSampleUnitFile = async () => {
  await downloadFile(
    "masters/units/sample-import-file/export",
    "unit-sample-excel.xlsx",
    () => {}, // success
    () => {}, // error
  )
}

watch(
  () => isImportUnitModal.value,
  value => {
    if (value === false) {
      importUnitFile.value = null
      importUnitErrors.value = null
    }
  },
)
</script>

<template>
  <section>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: 'All' },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 10rem;">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>

          <!-- 👉 Export button -->
          <!--
            <VBtn
            variant="tonal"
            color="secondary"
            prepend-icon="tabler-screen-share"
            >
            Export
            </VBtn> 
          -->

          <!-- 👉 Import Units Button -->
          <VBtn
            v-if="$can('import', 'master.unit')"
            prepend-icon="tabler-download"
            @click="isImportUnitModal = !isImportUnitModal"
          >
            Import Units
          </VBtn>
       
          <VBtn
            v-if="$can('create', 'master.unit')"
            prepend-icon="tabler-plus"
            @click="showUnit({},'Add')"
          >
            Add New Unit
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- SECTION datatable -->
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="units"
        :items-length="totalUnits"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <!-- User -->
        <template #item.name="{ item }">
          <div
            class="d-flex align-center cursor-pointer"
        
            :unitData="item"
            @click="showUnit(item,'View')"
          >
            <VImg
              v-if="item.avatar"
              :src="item.avatar"
              :max-width="30"
              class="me-2"
            />
            <span v-else>{{ avatarText(item.name) }}</span>
            
            <div class="d-flex flex-column">
              <h6 class="text-base">
                {{ item.name }}
              </h6>
            </div>
          </div>
        </template>
        <template #item.code="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.code }}</span>
        </template>
        
        <template #item.status="{ item }">
          <VChip
            v-if="item.status === 1"
            size="x-small"
            color="primary"
          >
            Active
          </VChip>
          
          <VChip
            v-else
            size="x-small"
            color="secondary"
          >
            Inactive
          </VChip>
        </template>
    
        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn 
            v-if="$can('delete', 'master.unit')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn 
            v-if="$can('update', 'master.unit')"
            @click="showUnit(item,'Edit')"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>

        <!-- pagination -->
        <!--
          <template #bottom>
          <VDivider />
          <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
          <p class="text-sm text-disabled mb-0">
          {{ paginationMeta({ page, itemsPerPage }, totalUnits) }}
          </p>

          <VPagination
          v-model="page"
          :length="Math.ceil(totalUnits / itemsPerPage)"
          :total-visible="$vuetify.display.xs ? 1 : Math.ceil(totalUnits / itemsPerPage)"
          >
          <template #prev="slotProps">
          <VBtn
          variant="tonal"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          Previous
          </VBtn>
          </template>

          <template #next="slotProps">
          <VBtn
          variant="tonal"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          Next
          </VBtn>
          </template>
          </VPagination>
          </div>
          </template> 
        -->
      </VDataTableServer>
      <!-- SECTION -->
    </VCard>  
    <UnitDrawer
      v-model:isDrawerOpen="isUnitDrawerVisible"
      :unit-data="unitDrawerData"
      :type="unitDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="unitDrawerType = $event"
      @update:unit-data="updateUnit"
      @save-unit="saveUnit"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Unit Deleted."
      confirm-title="Deleted!"
      :data="unitDeleteData"
      @confirm="deleteUnit"
    />

    <!-- Import Unit Modal -->
    <VDialog
      v-model="isImportUnitModal"
      max-width="600"
      :persistent="isImportUnitLoading"
    >
      <DialogCloseBtn
        size="small"
        :disabled="isImportUnitLoading"
        @click="isImportUnitModal = false"
      />
      <VCard>
        <VCardText class="pt-2">
          <h6 class="text-lg font-weight-medium mt-5">
            Import Units
          </h6>
        </VCardText>

        <VCardText>
          <VForm ref="refImportUnitForm">
            <VFileInput
              label="Upload File"
              prepend-icon="tabler-file-import"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              :disabled="isImportUnitLoading"
              :error-messages="importUnitErrors?.file"
              @change="
                (e) => {
                  importUnitFile = e.target.files[0];
                }
              "
            />
          </VForm>
        </VCardText>

        <!-- General Errors -->
        <VCardText v-if="importUnitErrors?.message && !Array.isArray(importUnitErrors)">
          <VAlert
            color="error"
            variant="tonal"
            closable
            @click:close="importUnitErrors.message = null"
          >
            {{ importUnitErrors.message }}
          </VAlert>
        </VCardText>

        <!-- Validation Errors (row-specific from backend) -->
        <VCardText v-if="Array.isArray(importUnitErrors) && importUnitErrors.length > 0">  
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="text-error text-caption font-weight-medium">
              {{ importUnitErrors.length }}
              {{ importUnitErrors.length === 1 ? 'Error' : 'Errors' }} found
            </div>
            <VBtn
              icon
              color="error"
              variant="text"
              size="small"
              class="ma-0 pa-0"
              density="compact"
              @click="importUnitErrors = []"
            >
              <VIcon
                color="error"
                size="18"
                icon="tabler-x"
              />
            </VBtn>
          </div>
          <div
            class="d-flex flex-column gap-2 scrollable-errors"
            style="max-height: 90px; overflow-y: auto;"
          >
            <VAlert
              v-for="(error, index) in importUnitErrors"
              :key="index"
              color="error"
              variant="tonal"
              density="compact"
              style="flex-shrink: 0;"
            >
              {{ error.row ? `Row ${error.row}: ` : "" }}{{ error.message || error }}
            </VAlert>
          </div>
        </VCardText>

        <!-- Loading Bar -->
        <VCardText
          v-if="isImportUnitLoading"
          class="pt-0 pb-2"
        >
          <VProgressLinear
            indeterminate
            color="primary"
            height="6"
            rounded
            class="mb-1"
          />
        </VCardText>

        <!-- Action Buttons -->
        <VCardActions class="px-5 pb-5">
          <VBtn
            variant="tonal"
            color="primary"
            :disabled="isImportUnitLoading"
            @click="downloadSampleUnitFile"
          >
            Download Sample
          </VBtn>
          <VSpacer />
          <VBtn
            variant="tonal"
            color="#EF5350"
            :disabled="isImportUnitLoading"
            @click="isImportUnitModal = false"
          >
            Close
          </VBtn>
          <VBtn
            variant="flat"
            :loading="isImportUnitLoading"
            :disabled="isImportUnitLoading || !importUnitFile"
            @click="submitImportUnit"
          >
            Import
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style lang="css">
/* For Chrome, Edge, Safari */
.scrollable-errors::-webkit-scrollbar {
  width: 2px;               /* thin scrollbar */
}

.scrollable-errors::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.2);  /* light color */
  border-radius: 2px;
}

.scrollable-errors::-webkit-scrollbar-track {
  background-color: transparent;      /* keep track transparent */
}

/* For Firefox */
.scrollable-errors {
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.2) transparent;
}
</style>
